import { t } from "@lingui/macro";
import {
  ArrowArcLeftIcon,
  ArrowArcRightIcon,
  BracketsCurlyIcon,
  DownloadSimpleIcon,
  FilePdfIcon,
  ListIcon,
  MoonIcon,
  PaintBrushBroadIcon,
  SquaresFourIcon,
  SunIcon,
  TextboxIcon,
} from "@phosphor-icons/react";
import { Button, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { BuilderLogo } from "./builder-logo";

/**
 * BuilderTopbar — v3 top bar (Apply Edge builder).
 *
 * Three groups:
 *   left   — logo + resume-name dropdown (chevron)
 *   center — Content / Design segmented tabs (the mode switch)
 *   right  — undo / redo · overflow menu · dark-mode · green Download · avatar
 *
 * Presentational + controlled. The SAVE status badge is intentionally NOT here —
 * it now lives above the preview (see RightPanel.statusSlot / SaveBadge).
 */
export type BuilderMode = "content" | "design";

export type BuilderMenuItem = {
  id: string;
  label: string;
  /** Optional secondary line under the label (e.g. usage hint). */
  description?: string;
  icon?: ReactNode;
  danger?: boolean;
  onSelect?: () => void;
};

export type BuilderTopbarProps = {
  resumeName: string;

  mode: BuilderMode;
  onModeChange: (mode: BuilderMode) => void;

  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  /** Overflow ("hamburger") menu items. Sensible defaults are provided if omitted. */
  menuItems?: BuilderMenuItem[];

  isDark?: boolean;
  onToggleTheme?: () => void;

  onDownload?: () => void;

  /** Avatar content — initials string, or a node for an <img>. */
  avatar?: ReactNode;
};

export const BuilderTopbar = ({
  resumeName,
  mode,
  onModeChange,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  menuItems,
  isDark = false,
  onToggleTheme,
  onDownload,
  avatar = "AE",
}: BuilderTopbarProps) => {
  return (
    <header className="grid h-14 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-border bg-background/90 px-3 backdrop-blur">
      {/* LEFT — logo + resume name */}
      <div className="flex min-w-0 items-center gap-2">
        {/* Two beta testers independently reported being trapped in the
            builder — the logo was decorative. It is now the way out. */}
        <Tooltip content={t`Back to resumes`}>
          <Link to="/dashboard/resumes" aria-label={t`Back to resumes`} className="shrink-0">
            <BuilderLogo />
          </Link>
        </Tooltip>
        <div className="flex min-w-0 max-w-[220px] items-center gap-2 rounded-md px-2.5 py-1.5 text-sm">
          <span className="truncate font-medium text-foreground">{resumeName}</span>
        </div>
      </div>

      {/* CENTER — Content / Design tabs */}
      <div
        role="tablist"
        aria-label={t`Builder mode`}
        className="relative flex rounded-full bg-secondary p-1"
      >
        <span
          aria-hidden
          className="absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-background shadow-sm transition-transform duration-200"
          style={{ transform: mode === "design" ? "translateX(100%)" : "translateX(0)" }}
        />
        {(["content", "design"] as const).map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={mode === value}
            className={cn(
              "relative z-10 flex items-center gap-1.5 rounded-full px-5 py-1.5 text-sm font-medium transition-colors",
              mode === value ? "text-foreground" : "text-muted-foreground",
            )}
            onClick={() => {
              onModeChange(value);
            }}
          >
            {value === "content" ? <TextboxIcon size={16} /> : <PaintBrushBroadIcon size={16} />}
            {value === "content" ? t`Content` : t`Design`}
          </button>
        ))}
      </div>

      {/* RIGHT — history · menu · theme · download · avatar */}
      <div className="flex items-center justify-end gap-1.5">
        <div className="flex items-center">
          <Tooltip content={t`Undo`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={!canUndo}
              aria-label={t`Undo`}
              onClick={onUndo}
            >
              <ArrowArcLeftIcon size={16} />
            </Button>
          </Tooltip>
          <Tooltip content={t`Redo`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={!canRedo}
              aria-label={t`Redo`}
              onClick={onRedo}
            >
              <ArrowArcRightIcon size={16} />
            </Button>
          </Tooltip>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t`Menu`}>
              <ListIcon size={18} />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-52 p-1.5">
            {(menuItems ?? []).map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={cn(
                  "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                  item.danger
                    ? "text-foreground hover:bg-error/10 hover:text-error"
                    : "text-foreground hover:bg-accent",
                )}
                onClick={item.onSelect}
              >
                {item.icon ? (
                  <span className="mt-0.5 text-muted-foreground">{item.icon}</span>
                ) : null}
                <span className="flex flex-col">
                  {item.label}
                  {item.description ? (
                    <span className="text-xs text-muted-foreground">{item.description}</span>
                  ) : null}
                </span>
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <Tooltip content={isDark ? t`Light mode` : t`Dark mode`}>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label={t`Toggle theme`}
            aria-pressed={isDark}
            onClick={onToggleTheme}
          >
            {isDark ? <SunIcon size={16} /> : <MoonIcon size={16} />}
          </Button>
        </Tooltip>

        {/* Green Download (v3) — uses the success token, not primary indigo.
            The strong/soft pair, not bg-success/text-success-foreground: that
            was emerald-50 on emerald-600, 3.58:1. This is 5.21 light / 7.90
            dark and still reads as the same green button. */}
        <Button
          size="sm"
          className="gap-1.5 border-0 bg-success-strong text-success-soft hover:bg-emerald-800"
          aria-label={t`Download`}
          onClick={onDownload}
        >
          <DownloadSimpleIcon size={15} />
          <span className="hidden sm:inline">{t`Download`}</span>
        </Button>

        <div className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-800 font-mono text-xs font-semibold text-white">
          {avatar}
        </div>
      </div>
    </header>
  );
};

/**
 * Default overflow-menu items. Pass your own via `menuItems` to override.
 * Exported so the container can spread/extend them.
 */
export const defaultBuilderMenuItems = (handlers: {
  onBrowseTemplates?: () => void;
  onExportJSON?: () => void;
  onDownloadAtsPdf?: () => void;
}): BuilderMenuItem[] => [
  {
    id: "templates",
    label: t`Browse templates`,
    icon: <SquaresFourIcon size={16} />,
    onSelect: handlers.onBrowseTemplates,
  },
  {
    id: "export-ats",
    label: t`Download ATS PDF`,
    description: t`Plain single-column version for job portals that scan resumes`,
    icon: <FilePdfIcon size={16} />,
    onSelect: handlers.onDownloadAtsPdf,
  },
  {
    id: "export-json",
    label: t`Export as JSON`,
    icon: <BracketsCurlyIcon size={16} />,
    onSelect: handlers.onExportJSON,
  },
];
